import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { publicApi } from '../services/api';
import useLiveKit from '../hooks/useLiveKit';
import VideoPlayer from '../components/VideoPlayer';
import Controls from '../components/Controls';
import Chat from '../components/Chat';

export default function RoomPage() {
  const { roomId } = useParams();
  const [roomInfo, setRoomInfo] = useState(null);
  const [userName, setUserName] = useState('');
  const [userRole, setUserRole] = useState('participant');
  const [joined, setJoined] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [chatToken, setChatToken] = useState('');
  const [myIdentity, setMyIdentity] = useState('');

  const {
    localStream, remoteStream, isRemoteConnected, connectionStatus, remoteName,
    localMic, localCam, remoteMic, remoteCam,
    connect, disconnect, toggleCamera, toggleMic, setProcessor,
  } = useLiveKit();

  useEffect(() => {
    const loadRoom = async () => {
      try {
        const info = await publicApi.getRoomInfo(roomId);
        if (info.error) { setError(info.error); return; }
        setRoomInfo(info);
      } catch { setError('Sala nao encontrada.'); }
      finally { setLoading(false); }
    };
    loadRoom();
  }, [roomId]);

  const handleJoin = async () => {
    if (!userName.trim()) return;
    setLoading(true);
    try {
      const data = await publicApi.joinRoom(roomId, userName.trim(), userRole);
      if (data.error) { setError(data.error); return; }
      if (data.chatToken) setChatToken(data.chatToken);
      if (data.participantIdentity) setMyIdentity(data.participantIdentity);
      await connect(data.token, data.wsUrl, data.iceServers);
      setJoined(true);
    } catch (err) { setError('Erro ao entrar na sala.'); }
    finally { setLoading(false); }
  };

  const handleLeave = () => {
    disconnect();
    setJoined(false);
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center text-white">
      <div className="text-center">
        <p className="text-xl mb-4">{error}</p>
        <a href="/" className="text-indigo-400 hover:underline">Voltar</a>
      </div>
    </div>
  );

  if (!joined) return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
      <div className="bg-gray-800 rounded-2xl p-8 w-full max-w-md shadow-2xl">
        {roomInfo?.branding?.logo && (
          <img src={roomInfo.branding.logo} alt="" className="h-12 mx-auto mb-4" />
        )}
        <h1 className="text-white text-2xl font-bold text-center mb-2">
          {roomInfo?.displayName || 'Sala de Video'}
        </h1>
        <p className="text-gray-400 text-center mb-6 text-sm">
          {roomInfo?.branding?.clientName || 'Video Call'}
        </p>

        <div className="space-y-4">
          <input
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleJoin()}
            placeholder="Seu nome"
            className="w-full bg-gray-700 text-white rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-indigo-500"
          />

          <select
            value={userRole}
            onChange={(e) => setUserRole(e.target.value)}
            className="w-full bg-gray-700 text-white rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="participant">Participante</option>
            <option value="host">Anfitriao</option>
          </select>

          <button
            onClick={handleJoin}
            disabled={!userName.trim()}
            className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-50"
            style={{ backgroundColor: roomInfo?.branding?.primaryColor || undefined }}
          >
            Entrar na Sala
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-screen bg-gray-900 flex flex-col">
      {/* Video area */}
      <div className="flex-1 flex overflow-hidden">
        <div className={`flex-1 flex ${chatOpen ? 'mr-0' : ''}`}>
          <div className="flex-1 relative p-2">
            {/* Remote video (main) */}
            <VideoPlayer
              stream={remoteStream}
              label={remoteName || 'Participante'}
              className="w-full h-full"
              micOn={remoteMic}
              camOn={remoteCam}
            />
            {/* Local video (pip) */}
            <div className="absolute bottom-4 right-4 w-40 md:w-52 aspect-video z-10">
              <VideoPlayer
                stream={localStream}
                muted
                label="Voce"
                className="w-full h-full border-2 border-gray-600 shadow-lg"
                micOn={localMic}
                camOn={localCam}
              />
            </div>
            {/* Status */}
            <div className="absolute top-3 left-3 flex items-center gap-2">
              <span className={`w-2.5 h-2.5 rounded-full ${
                connectionStatus === 'connected' ? 'bg-green-500' : 'bg-yellow-500 animate-pulse'
              }`}></span>
              <span className="text-white text-xs bg-black/50 px-2 py-1 rounded">
                {connectionStatus === 'connected' ? 'Conectado' : 'Conectando...'}
              </span>
            </div>
          </div>
        </div>

        {/* Chat sidebar */}
        {chatOpen && chatToken && (
          <div className="w-80 flex-shrink-0">
            <Chat
              roomId={roomId}
              userName={userName}
              userRole={userRole}
              isOpen={chatOpen}
              onToggle={() => setChatOpen(false)}
              chatToken={chatToken}
              myIdentity={myIdentity}
            />
          </div>
        )}
      </div>

      {/* Controls */}
      <Controls
        onToggleMic={toggleMic}
        onToggleCamera={toggleCamera}
        onLeave={handleLeave}
        onToggleChat={() => setChatOpen(!chatOpen)}
        chatOpen={chatOpen}
      />
    </div>
  );
}
